import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete, Phone, Email } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { CustomerService } from '../services/customerService';
import { Customer } from '../types';

interface CustomerFormData {
  name: string;
  phone: string;
  email: string;
  totalSpent: number;
  visitCount: number;
}

export default function Customers() {
  const { appUser } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  const [formData, setFormData] = useState<CustomerFormData>({
    name: '',
    phone: '',
    email: '',
    totalSpent: 0,
    visitCount: 0,
  });

  const loadCustomers = async () => {
    if (!appUser?.salonId) {
      setError('User not properly authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const customerData = await CustomerService.getCustomers(appUser.salonId);
      setCustomers(customerData);
      setError(null);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [appUser]);

  const handleOpenDialog = (customer?: Customer) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name,
        phone: customer.phone || '',
        email: customer.email || '',
        totalSpent: customer.totalSpent,
        visitCount: customer.visitCount,
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        totalSpent: 0,
        visitCount: 0,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCustomer(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      totalSpent: 0,
      visitCount: 0,
    });
  };

  const handleSubmit = async () => {
    if (!appUser?.salonId) return;

    try {
      if (editingCustomer) {
        await CustomerService.updateCustomer(editingCustomer.id, {
          name: formData.name,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
          totalSpent: formData.totalSpent,
          visitCount: formData.visitCount,
        });
        setSuccess('Customer updated successfully!');
      } else {
        await CustomerService.addCustomer(appUser.salonId, {
          name: formData.name,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
        });
        setSuccess('Customer added successfully!');
      }

      await loadCustomers();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving customer:', err);
      setError('Failed to save customer');
    }
  };

  const handleDelete = async (customerId: string) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      await CustomerService.deleteCustomer(customerId);
      setSuccess('Customer deleted successfully!');
      await loadCustomers();
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError('Failed to delete customer');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Never';
    return date.toLocaleDateString('en-NG');
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Customer Management
        </Typography>
        <Typography>Loading customers...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Customer Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Customer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Total Spent</TableCell>
              <TableCell>Visits</TableCell>
              <TableCell>Last Visit</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No customers found. Add your first customer to get started.
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{customer.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <Phone fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">{customer.phone}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Email fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">{customer.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(customer.totalSpent)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${customer.visitCount} visits`}
                      size="small"
                      color={customer.visitCount > 5 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(customer.lastVisit)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(customer)}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(customer.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Phone"
            fullWidth
            variant="outlined"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email"
            fullWidth
            variant="outlined"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCustomer ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
